import React, { forwardRef, useEffect } from 'react';
import styles from './DocumentForm.module.scss';
import { Form } from 'antd';
import FilesBlock from '../FilesBlock/FilesBlock';
import DocumentInfoContainer from '../DocumentInfoContainer/DocumentInfoContainer';
import ContactorTypeContainer from '../ContactorTypeContainer/ContactorTypeContainer';
import AdditionallyContainer from '../AdditionallyContainer/AdditionallyContainer';
import ResponsiblePersonsContainer from '../ResponsiblePersonsContainer/ResponsiblePersonsContainer';
import VisibilityDocumentContainer from '../VisibilityDocumentContainer/VisibilityDocumentContainer';
import { docTypes } from '../../constants/documents.constants';
import DateNumberContainer from '../DateNumberContainer/DateNumberContainer';
import AddressMKD from '../AddressMKD/AddressMKD';
import Addressee from '../Addressee/Addressee';
import Organizations from '../Organizations/Organizations';
import { documentDrawerSelector } from '../../store/selectors/new-document.selector';
import { useSelector } from 'react-redux';
import { getMappedFormValues } from '../../utils/getMappedFormValues';

const DocumentFormInner = forwardRef(
  ({ form, handleFilesChange, onChangePreview }, ref) => {
    ref.current = form;

    const { getFieldValue, getFieldDecorator, resetFields, setFieldsValue } =
      form;

    const { visible, documentId, formValues, mode, savedDraft } = useSelector(
      documentDrawerSelector
    );

    const docType = getFieldValue('typeId');
    const contractorType = getFieldValue('contractorType');
    const subTypeId = getFieldValue('subTypeId') ?? 4;

    const showAddressee =
      ([docTypes.outgoing, docTypes.incoming, docTypes.citizenAppeal].includes(
        docType
      ) &&
        contractorType !== 'org_data') ||
      (docType === docTypes.contract &&
        subTypeId === 7 &&
        contractorType !== 'org_data');

    const showAdressMKD = [
      docTypes.outgoing,
      docTypes.incoming,
      docTypes.contract,
      docTypes.meeting,
      docTypes.citizenAppeal,
    ].includes(docType);

    const showOrganizations =
      ([docTypes.outgoing, docTypes.incoming].includes(docType) &&
        contractorType === 'org_data') ||
      (docType === docTypes.contract && subTypeId !== 7);

    useEffect(() => {
      if (!visible) {
        return;
      }

      const { baseValues, deferredValues } = getMappedFormValues({
        mode,
        formValues,
        savedDraft,
      });

      resetFields();
      setFieldsValue(baseValues);

      const timerId = setTimeout(() => {
        if (mode === 'edit' || !!savedDraft?.values)
          setFieldsValue(deferredValues);
      }, 0);

      return () => clearTimeout(timerId);
    }, [
      visible,
      mode,
      documentId,
      formValues,
      savedDraft,
      resetFields,
      setFieldsValue,
    ]);

    return (
      <Form>
        <div className={styles.container}>
          <div className={styles.column}>
            <FilesBlock
              form={form}
              handleFilesChange={handleFilesChange}
              onChangePreview={onChangePreview}
            />
            <DocumentInfoContainer form={form} />
            <DateNumberContainer form={form} />
            <AdditionallyContainer form={form} />
          </div>
          <div className={styles.column}>
            <ContactorTypeContainer form={form} />

            {getFieldDecorator('custNameKeys')}
            {showAddressee && <Addressee form={form} />}

            {getFieldDecorator('orgsKeys')}
            {showOrganizations && <Organizations form={form} />}

            {getFieldDecorator('addressKeys')}
            {showAdressMKD && <AddressMKD form={form} />}

            <ResponsiblePersonsContainer form={form} />
            <VisibilityDocumentContainer form={form} />
          </div>
        </div>
      </Form>
    );
  }
);

const DocumentForm = Form.create('document_form')(DocumentFormInner);

export default DocumentForm;
